import React, { Dispatch } from "react";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { FeedbackCategory } from "../../../../Feedback";
import { IAppState, ErrorDisplay } from "../../../../Common";
import SubscribeToAppButton from "./SubscribeToAppButton";
import {
  IGetAppSubscriptionStatusArguments,
  ISetAppSubscriptionStatusLoadingArguments,
  IUnloadAppSubscriptionStatusArguments,
  AppSubscriptionAction,
  getAppSubscriptionStatus,
  setAppSubscriptionStatusLoading,
  unloadAppSubscriptionStatus
} from "../../../Actions/AppSubscriptionActions";

type Props = {
  appId: string;
};

type StateProps = {
  appName?: string;
  isSubscribed?: boolean;
  category?: FeedbackCategory;
  isLoading: boolean;
  isLoaded: boolean;
  isErrored: boolean;
  errorMessage?: string;
  errorCode?: number;
};

type DispatchProps = {
  getAppSubscriptionStatus: (args: IGetAppSubscriptionStatusArguments) => void;
  setAppSubscriptionStatusLoading: (args: ISetAppSubscriptionStatusLoadingArguments) => void;
  unloadAppSubscriptionStatus: (args: IUnloadAppSubscriptionStatusArguments) => void;
};

function mapStateToProps(state: IAppState, props: Props): StateProps {
  if (state.appSubscriptions.subscriptionStates[props.appId] !== undefined) {
    return {
      appName: state.appSubscriptions.subscriptionStates[props.appId].appName,
      isSubscribed: state.appSubscriptions.subscriptionStates[props.appId].isSubscribed,
      category: state.appSubscriptions.subscriptionStates[props.appId].category,
      isLoading: state.appSubscriptions.subscriptionStates[props.appId].isLoading,
      isLoaded: state.appSubscriptions.subscriptionStates[props.appId].isLoaded,
      isErrored: state.appSubscriptions.subscriptionStates[props.appId].isErrored,
      errorMessage: state.appSubscriptions.subscriptionStates[props.appId].errorMessage,
      errorCode: state.appSubscriptions.subscriptionStates[props.appId].errorCode
    };
  }
  // the record for this app does not exist, assume a default value
  return {
    isLoaded: false,
    isLoading: false,
    isErrored: false
  };
}

function mapDispatchToProps(
  dispatch: ThunkDispatch<IAppState, {}, AppSubscriptionAction>
): DispatchProps {
  return {
    getAppSubscriptionStatus: (args: IGetAppSubscriptionStatusArguments) =>
      dispatch(getAppSubscriptionStatus(args)),
    setAppSubscriptionStatusLoading: (args: ISetAppSubscriptionStatusLoadingArguments) =>
      dispatch(setAppSubscriptionStatusLoading(args)),
    unloadAppSubscriptionStatus: (args: IUnloadAppSubscriptionStatusArguments) =>
      dispatch(unloadAppSubscriptionStatus(args))
  };
}

class SubscribeToAppButtonContainer extends React.Component<Props & StateProps & DispatchProps> {
  constructor(props: Props & StateProps & DispatchProps) {
    super(props);

    this.onSubscribe = this.onSubscribe.bind(this);
    this.onUnsubscribe = this.onUnsubscribe.bind(this);
  }

  componentDidMount() {
    this.loadIfNeeded();
  }

  componentDidUpdate() {
    this.loadIfNeeded();
  }

  componentWillUnmount() {
    this.props.unloadAppSubscriptionStatus({ appId: this.props.appId });
  }

  onSubscribe(category?: FeedbackCategory) {
    this.reload();
  }

  onUnsubscribe() {
    this.reload();
  }

  // dispatch the actions to load the subscription state
  reload() {
    this.props.setAppSubscriptionStatusLoading({ ...this.props });
    this.props.getAppSubscriptionStatus({ ...this.props });
  }

  // loads the subscription state if it has not been loaded
  loadIfNeeded() {
    if (!this.props.isLoaded && !this.props.isLoading && !this.props.isErrored) {
      this.reload();
    }
  }

  render() {
    if (this.props.isErrored) {
      return (
        <ErrorDisplay
          errorCode={this.props.errorCode}
          errorDescription={this.props.errorMessage}
          errorResult="Could not display Subscribe to App button"
        />
      );
    }
    if (this.props.isLoaded || this.props.isLoading) {
      return (
        <SubscribeToAppButton
          {...this.props}
          onSubscribe={this.onSubscribe}
          onUnsubscribe={this.onUnsubscribe}
        />
      );
    }
    return <></>;
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SubscribeToAppButtonContainer);
